/**
 * «Расписание» — экран няни.
 *
 * Читает то же, что видит родитель, но здесь можно отметить выполнение
 * или честно сказать «не получилось» — с причиной, которую родитель увидит
 * вместо тишины.
 */

import { useState } from 'react'

import { useFamily, useTimeZone } from '@/app/session'
import { TaskList } from '@/features/schedule/TaskList'
import { WeekStrip } from '@/features/schedule/WeekStrip'
import { SkipSheet } from '@/features/schedule/SkipSheet'
import { useCompleteTask, useSchedule, type ScheduleTask } from '@/shared/api/schedule'
import { familyDate, formatDayLabel } from '@/shared/format'
import { haptic } from '@/shared/telegram'
import { Screen } from '@/shared/ui/Screen'
import { SectionTitle } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

function weekStart(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  const iso = parsed.getUTCDay() === 0 ? 7 : parsed.getUTCDay()
  parsed.setUTCDate(parsed.getUTCDate() - (iso - 1))
  return parsed.toISOString().slice(0, 10)
}

export function NannySchedule() {
  const family = useFamily()
  const timeZone = useTimeZone()
  const familyId = family?.id ?? 0

  const today = familyDate(new Date(), timeZone)
  const [selected, setSelected] = useState(today)
  const [skipping, setSkipping] = useState<ScheduleTask | null>(null)

  const start = weekStart(selected)
  const schedule = useSchedule(familyId, start, 7)

  const complete = useCompleteTask(familyId, {
    onSuccess: (task) => {
      haptic('success')
      toast.success(`${task.emoji} ${task.title} — отмечено`)
    },
    onError: (error) => toast.error(error.message),
  })

  if (!family) return null
  if (schedule.isLoading) {
    return (
      <Screen title="Расписание">
        <SkeletonCard lines={2} />
        <SkeletonCard lines={3} />
      </Screen>
    )
  }
  if (schedule.error) {
    return (
      <Screen title="Расписание">
        <ErrorState error={schedule.error} onRetry={() => void schedule.refetch()} />
      </Screen>
    )
  }

  const data = schedule.data!
  const dayTasks = data.tasks.filter((task) => task.date === selected)

  return (
    <Screen title="Расписание">
      <WeekStrip start={start} selected={selected} tasks={data.tasks} onSelect={setSelected} />

      <div>
        <SectionTitle>{formatDayLabel(selected, timeZone)}</SectionTitle>
        <TaskList
          tasks={dayTasks}
          canComplete
          busyId={complete.isPending ? complete.variables : null}
          onComplete={(task) => complete.mutate(task.id)}
          onSkip={setSkipping}
          emptyTitle="На этот день задач нет"
          emptyDescription="Родители пока ничего не запланировали."
        />
      </div>

      <SkipSheet
        task={skipping}
        familyId={familyId}
        onClose={() => setSkipping(null)}
      />
    </Screen>
  )
}
